import { Switch, Text, VStack } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useCampaignDetail, useSetCampaignFieldProfile } from '../../../hooks/useCampaign'
import { extractApiMessage } from '../../../lib/apiError'
import { Card } from '../../shared'

// L11 slice 1e, Task 16: campaign-level default field profile toggle -- controls whether newly
// minted canvasser access grants show partisanship/turnout scores ('WithAnalytics') or not
// ('Minimal', the default). Off by default; a genuinely [LEGAL] posture (scores are hidden from
// field staff unless an organizer opts in).
//
// CRITICAL: useSetCampaignFieldProfile's mutationFn forwards `current` straight into
// campaignCanvasserApi.setFieldProfile, which reuses the campaign UPDATE endpoint and
// unconditionally overwrites description/refreshStrategyOverride (see lib/api.ts comment on
// setFieldProfile). This component therefore reads the campaign's own CampaignDetailDto via
// useCampaignDetail first and always passes the FULL current object through -- there is no
// partial-body path available, so skipping this read would clobber other campaign fields on
// every toggle.

interface FieldProfileToggleProps {
  campaignId: string
}

export function FieldProfileToggle({ campaignId }: FieldProfileToggleProps) {
  const { data: campaign, isLoading } = useCampaignDetail(campaignId)
  const setFieldProfile = useSetCampaignFieldProfile(campaignId)

  const isOn = campaign?.defaultFieldProfile === 'WithAnalytics'

  const handleToggle = async (checked: boolean) => {
    if (!campaign) return
    try {
      await setFieldProfile.mutateAsync({ current: campaign, profile: checked ? 'WithAnalytics' : 'Minimal' })
    } catch (err) {
      toaster.create({ type: 'error', title: extractApiMessage(err, 'Failed to update field profile.') })
    }
  }

  return (
    <Card>
      <VStack align="stretch" gap={2}>
        <Switch.Root
          size="md"
          checked={isOn}
          disabled={isLoading || !campaign || setFieldProfile.isPending}
          onCheckedChange={(e) => handleToggle(e.checked === true)}
          colorPalette="brand"
        >
          <Switch.HiddenInput />
          <Switch.Control _touch={{ minH: '2.75rem', minW: '2.75rem' }}>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label>
            <Text fontWeight="medium">Show partisanship / turnout scores to canvassers</Text>
          </Switch.Label>
        </Switch.Root>
        <Text fontSize="xs" color="muted">
          Off by default — scores stay hidden from canvassers.
        </Text>
      </VStack>
    </Card>
  );
}
